import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useMeasurement, useSirenEntity, muiTheme } from '~/utility/BinSentry-ui-utility';
import { makeStyles, Paper, Popper, Typography } from '~/lib/bn-material-ui';
import { useId } from '~/common/use-id';
import classNames from 'classnames';
import { CircularBinDimensions, BinProfiles, BinVolumes } from '~/bins/types/bin-entity';
import { FeedEntity } from '~/feeds/types/feed-entity';

const PADDING_RATIO = 0.05;

type SvgPoint = {
  x: number;
  y: number;
};

type FeedVolume = {
  feedId?: string;
  volume: number;
  color: string;
};

// plot coordinates of the bin
// + x
// y        t
//       |-----|
//      0       1        -      -
//       .--=--.         | ht   |
//   7 .`-------`. 2     -      |
//     |         |       | hm   | h
//   6 . _______ . 3     -      |
//       `.___.`         | hb   |
//      5       4        -      -
//        |___|
//          b
//     |_________|
//          w
/* eslint-disable no-multi-spaces */

enum BinPolyPoints {
  TOP_LEFT = 0,
  TOP_RIGHT = 1,
  BODY_TOP_RIGHT = 2,
  BODY_BOTTOM_RIGHT = 3,
  HOPPER_BOTTOM_RIGHT = 4,
  HOPPER_BOTTOM_LEFT = 5,
  BODY_BOTTOM_LEFT = 6,
  BODY_TOP_LEFT = 7,
}

type onMouseOverFeedEvent = (event: React.MouseEvent<SVGRectElement, MouseEvent>, feedId?: string) => void;

const useStyles = makeStyles<typeof muiTheme>((theme) => ({
  polygonOutline: {
    stroke: theme.palette.gray[60],
    fill: 'none',
  },
  polygonBin: {
    fill: theme.palette.gray[40],
  },
  lineLegs: {
    stroke: theme.palette.gray[50],
    strokeDasharray: 'none',
    pointerEvents: 'none',
  },
  lineRivets: {
    stroke: theme.palette.gray[70],
    strokeDasharray: '1, 2',
    pointerEvents: 'none',
    filter: 'drop-shadow( 0px 0px 0px white)',
  },
  sectionNumber: {
    fontSize: '0.8rem',
    pointerEvents: 'none',
  },
  sectionOutline: {
    fontSize: '0.8rem',
    stroke: 'white',
    strokeWidth: '2px',
    strokeLinejoin: 'round',
  },
  popperPaper: {
    padding: '.5em',
    boxShadow: theme.shadows[4],
    position: 'relative',
    minWidth: '20rem',
    '& > div': {
      width: '100%',
    },
  },
  popper: {
    zIndex: theme.zIndex.modal + 1,
    width: '15rem',
  },
  feedLevel: {
    strokeWidth: 0,
  },
}));

export function binDimensionsArePlottable(binDimensions: CircularBinDimensions) {
  const { totalHeight, top, body, hopper } = binDimensions;
  return totalHeight && top && body && hopper;
}

export function calculateScale({
  binDimensions,
  heightPx,
  widthPx,
  additionalLegHeight = 0,
} : {
  binDimensions?: CircularBinDimensions,
  heightPx: number,
  widthPx: number,
  additionalLegHeight: number,
}) : number {
  if (!binDimensions || !binDimensionsArePlottable(binDimensions)) {
    return 0;
  }

  const { totalHeight, body } = binDimensions;
  const h = totalHeight;
  const w = (body.radius || 0) * 2;

  const padding = calculatePadding(widthPx, heightPx);

  return Math.min((heightPx - padding.y - additionalLegHeight) / h, (widthPx - padding.x) / w);
}

/**
 * @param heightPx
 * @param widthPx
 * @param feeds
 * @param binVolumes
 * @param binDimensions
 * @param additionalLegHeight
 * @param hideRings
 * @param verticalAlign {'bottom' | 'middle' | 'top'} - vertical alignment of the bin within the svg
 * @param horizontalAlign {'left' | 'center' | 'right'} - horizontal alignment of the bin within the svg
 * @param scale
 * @param binPolygonClassName class which effect the inner shape of the bin
 * @returns {JSX.Element}
 * @constructor
 */
export function BinCircularProfileSvg({
  heightPx,
  widthPx,
  feeds,
  binVolumes,
  binDimensions,
  additionalLegHeight = 0,
  hideRings,
  hideRingLabels = hideRings,
  verticalAlign = 'middle',
  horizontalAlign = 'center',
  scale: providedScale,
  svgClassName = undefined,
  binPolygonClassName = undefined,
}: {
  heightPx: number;
  widthPx: number;
  feeds: FeedVolume[];
  binVolumes: BinVolumes;
  binDimensions?: CircularBinDimensions;
  additionalLegHeight?: number;
  hideRings?: boolean;
  hideRingLabels?: boolean;
  verticalAlign?: 'bottom' | 'middle' | 'top';
  horizontalAlign?: 'left' | 'center' | 'right';
  scale?: number;
  binPolygonClassName?: string;
  svgClassName?: string;

}) {
  const styles = useStyles();
  const [popoverAnchor, setPopoverAnchor] = useState<SVGPathElement | undefined>(undefined);
  const [popoverFeedId, setPopoverFeedId] = useState<string | undefined>(undefined);
  const onMouseOverFeed: onMouseOverFeedEvent = (event, feedId) => {
    if (feedId) {
      setPopoverFeedId(feedId);
      setPopoverAnchor(event.target as SVGPathElement);
    }
  };

  const points = useMemo(() => {
    if (!binDimensions || !binDimensionsArePlottable(binDimensions)) {
      return;
    }

    const { profile, totalHeight, top, body, hopper } = binDimensions;

    // calculate dimensions
    const h = totalHeight;
    const w = (body.radius || 0) * 2;
    const t = (top.truncatedRadius || 0) * 2;
    const b = (hopper.truncatedRadius || 0) * 2;
    const ht = top.height || 0;
    const hb = hopper.height || 0;

    const points = [
      { x: (w - t) / 2, y: 0 },
      { x: (w - t) / 2 + t, y: 0 },
      { x: w, y: ht },
      { x: w, y: h - hb },
      { x: (w - b) / 2 + b, y: h },
      { x: (w - b) / 2, y: h },
      { x: 0, y: h - hb },
      { x: 0, y: ht },
    ];

    // adjust for profile
    if (profile === BinProfiles.SIDE) {
      points[BinPolyPoints.HOPPER_BOTTOM_RIGHT].x = b;
      points[BinPolyPoints.HOPPER_BOTTOM_LEFT].x = 0;
    }

    const padding = calculatePadding(widthPx, heightPx);
    const scale = providedScale || calculateScale({ binDimensions, heightPx, widthPx, additionalLegHeight });

    function getVerticalOffset() {
      switch (verticalAlign) {
        case 'bottom':
          return heightPx - h * scale - padding.y;
        case 'middle':
          return (heightPx - h * scale - padding.y) / 2;
        default:
          return padding.y;
      }
    }

    function getHorizontalOffset() {
      switch (horizontalAlign) {
        case 'right':
          return widthPx - w * scale - padding.x;
        case 'center':
          return (widthPx - w * scale - padding.x) / 2;
        default:
          return padding.x;
      }
    }

    const verticalOffset = getVerticalOffset();
    const horizontalOffset = getHorizontalOffset();
    points.forEach((p) => {
      p.x = roundAndTrim(p.x * scale + horizontalOffset, 1, widthPx - 1);
      p.y = roundAndTrim(p.y * scale + verticalOffset, 1, heightPx - 1);
    });
    return points;
  }, [binDimensions, heightPx, widthPx, verticalAlign, horizontalAlign, additionalLegHeight, providedScale]);

  if (!points) {
    return <div />;
  }
  const polyPoints = getPolyPoints(points);
  return (
    <svg
      viewBox={`0 0 ${widthPx} ${heightPx + additionalLegHeight}`}
      className={svgClassName}
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
      }}
      onMouseLeave={() => setPopoverAnchor(undefined)}
    >
      <polygon
        className={classNames(binPolygonClassName, styles.polygonBin)}
        points={polyPoints}
      />
      <BinProfileFill
        points={points}
        feeds={feeds}
        binVolumes={binVolumes}
        widthPx={widthPx}
        onMouseOverFeed={onMouseOverFeed}
      />
      <BinProfileLegs
        points={points}
        additionalLegHeight={additionalLegHeight}
        styles={styles}
      />
      <BinProfileRings
        points={points}
        hideRings={hideRings}
        hideRingLabels={hideRingLabels}
        binDimensions={binDimensions}
        styles={styles}
      />
      <polygon className={styles.polygonOutline} points={polyPoints} />
      <Popper
        open={popoverAnchor !== undefined}
        anchorEl={popoverAnchor}
        placement='right-start'
        className={styles.popper}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              boundariesElement: 'scrollParent',
            },
          },
        ]}
      >
        <Paper className={styles.popperPaper}>
          <BinProfileFeedPopover
            feedId={popoverFeedId}
            feeds={feeds}
          />
        </Paper>
      </Popper>
    </svg>
  );
}

const BinProfileLegs = ({
  points,
  additionalLegHeight,
  styles,
} : {
  points: SvgPoint[];
  additionalLegHeight: number;
  styles: Record<string, string>;
}) => {
  if (!points) {
    return <div />;
  }
  const p1 = points[BinPolyPoints.BODY_BOTTOM_LEFT];
  const p2 = points[BinPolyPoints.BODY_BOTTOM_RIGHT];
  const bottom = points[BinPolyPoints.HOPPER_BOTTOM_RIGHT].y + additionalLegHeight;
  return <>
    <line className={styles.lineLegs} x1={p1.x} y1={p1.y} x2={p1.x} y2={bottom} />
    <line className={styles.lineLegs} x1={p2.x} y1={p2.y} x2={p2.x} y2={bottom} />
  </>;
};

const BinProfileRings = ({
  binDimensions,
  hideRings,
  hideRingLabels,
  points,
  styles,
} : {
  binDimensions?: CircularBinDimensions;
  hideRings?: boolean;
  hideRingLabels?: boolean;
  points: SvgPoint[];
  styles: Record<string, string>;
}) => {
  if (!binDimensions || (hideRings && hideRingLabels) || !points) {
    return null;
  }
  const { ringCount, top, hopper } = binDimensions;
  // get the top left and bottom right points of the body
  // p1 *---*
  //    |   |
  //    *---* p2
  const p1 = points[BinPolyPoints.BODY_TOP_LEFT];
  const p2 = points[BinPolyPoints.BODY_BOTTOM_RIGHT];
  const left = p1.x + 1;
  const right = p2.x - 1;
  const ringHeight = (p2.y - p1.y) / ringCount;
  const dx = Number(p2.x) - Number(p1.x);
  const x = Number(p1.x) + (dx / 2);

  return <>
    <defs key='ring-defs'>
      <radialGradient id='backgroundCircle'>
        <stop offset='10%' stopColor='rgba(255, 255, 255, 0.5)' />
        <stop offset='100%' stopColor='rgba(255, 255, 255, 0)' />
      </radialGradient>
    </defs>
    {top.height &&
      <line key='top' className={styles.lineRivets} x1={left} y1={p1.y} x2={right} y2={p1.y} />
    }
    {!hideRings ? _.range(ringCount).map(i => { // and body segments
      const y = Number(p1.y) + (i * ringHeight);
      return <line key={`ring-line${i}`} className={styles.lineRivets} x1={left} y1={y} x2={right} y2={y} />;
    }) : null}
    {!hideRingLabels ? _.range(ringCount).map(i => { // and body segments
      const y = Number(p1.y) + (i * ringHeight);
      return <svg key={`ring-text${i}`}>
        <text className={styles.sectionOutline} textAnchor='middle' x={x} y={y + (ringHeight / 2) + 5}>{ringCount - i}</text>
        <text className={styles.sectionNumber} textAnchor='middle' x={x} y={y + (ringHeight / 2) + 5}>{ringCount - i}</text>
      </svg>;
    }) : null}
    {hopper.height && // add hopper
      <line key='hopper' className={styles.lineRivets} x1={left} y1={p2.y} x2={right} y2={p2.y} />
    }
  </>;
};

const BinProfileFill = ({
  points,
  feeds,
  binVolumes,
  widthPx,
  onMouseOverFeed,
} : {
  points: SvgPoint[];
  feeds: FeedVolume[];
  binVolumes: BinVolumes;
  widthPx: number;
  onMouseOverFeed: onMouseOverFeedEvent;
}) => {
  const styles = useStyles();
  const clipPathId = useId();

  const _getBinSections = () => {
    return [
      {
        name: 'hopper',
        volume: _.clone(binVolumes.hopper),
        height: points[BinPolyPoints.HOPPER_BOTTOM_RIGHT].y - points[BinPolyPoints.BODY_BOTTOM_RIGHT].y,
      },
      {
        name: 'body',
        volume: _.clone(binVolumes.body),
        height: points[BinPolyPoints.BODY_BOTTOM_RIGHT].y - points[BinPolyPoints.BODY_TOP_RIGHT].y,
      },
      {
        name: 'top',
        volume: _.clone(binVolumes.top),
        height: points[BinPolyPoints.BODY_TOP_RIGHT].y,
      },
    ];
  };

  const _getFeedHeights = () => {
    if (feeds) {
      const feedVolumes = feeds.map(feed => feed.volume);
      const feedHeights = new Array(feedVolumes.length).fill(0);
      const svgHeight = points[4].y;
      const sections = _getBinSections();
      for (let i = 0; i < feedVolumes.length; i++) {
        for (const section of sections) {
          if (feedVolumes[i] < section.volume) {
            const usedHeight = (feedVolumes[i] / section.volume) * section.height;
            feedHeights[i] += usedHeight;
            section.volume -= feedVolumes[i];
            section.height -= usedHeight;
            break;
          } else {
            feedHeights[i] += section.height;
            feedVolumes[i] -= section.volume;
            section.height = 0;
            section.volume = 0;
          }
        }
      }
      const results = [];
      for (let i = 0; i < feedHeights.length; i++) {
        results.push({ height: feedHeights[i], y: 0 });
        let totalHeight = 0;
        for (let r = 0; r <= i; r++) {
          totalHeight += results[r].height;
        }
        results[i].y = svgHeight - totalHeight;
      }
      return results;
    }
  };

  if (feeds) {
    const feedHeights = _getFeedHeights();
    if (!feedHeights) {
      return null;
    }
    const x = points[7].x;
    return <>
      <clipPath id={clipPathId}>
        <polygon points={getPolyPoints(points)} />
      </clipPath>

      {feeds.map((feed, i) =>
        <React.Fragment key={i}>
          <rect
            className={styles.feedLevel}
            x={x}
            y={feedHeights[i].y}
            width={widthPx}
            height={feedHeights[i].height}
            clipPath={`url('#${clipPathId}')`}
            fill={feed.color}
            onMouseEnter={(e) => onMouseOverFeed(e, feed.feedId)}
          />
          <line x1={x} y1={feedHeights[i].y} x2={widthPx} y2={feedHeights[i].y} stroke={feed.color} clipPath={`url('#${clipPathId}')`}/>
        </React.Fragment>,
      )}
    </>;
  }
  return null;
};

const BinProfileFeedPopover = ({ feedId, feeds } : { feedId?: string, feeds: FeedVolume[] }) => {
  const { entity: feedEntity } = useSirenEntity<FeedEntity>({ href: feedId });
  const { feedType, bulkDensity } = feedEntity?.properties || {};
  const feed = feeds.find((feed: FeedVolume) => feed.feedId === feedId && feed.volume > 0);
  const orderMeasurement = useMeasurement({ name: 'orderAmount' });

  return <div>
    <Typography >Feed: {feedType}</Typography>
    <Typography >Volume: {orderMeasurement.convert({ volume: feed?.volume || 0, bulkDensity }) }{orderMeasurement.unit}</Typography>
  </div>;
};

function calculatePadding(widthPx: number, heightPx: number) {
  return {
    x: Math.max(Math.round((widthPx * PADDING_RATIO) / 2), 1), // at least one pixel to leave space for two pixels wide outline
    y: Math.max(Math.round((heightPx * PADDING_RATIO) / 2), 1), // at least one pixel to leave space for two pixels wide outline
  };
}

function getPolyPoints(points: SvgPoint[]) {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

/**
 * @returns {number} - rounded number at least min and at most max
 */
function roundAndTrim(number: number, min: number, max: number) {
  return Math.min(Math.max(Math.round(number), min), max);
}
